/**
 * Session 19d — Display Messages tab (OCPP 2.0.1-only). Lists the messages
 * the CSMS has pushed via SetDisplayMessage — the text a real CP would
 * render on its LCD. Each row has an "Acknowledge" button (local clear,
 * for UX validation). Read-only otherwise: messages arrive from the CSMS.
 */
import { useEffect } from 'react';
import { useStationStore } from '@/state/useStationStore';
import { useDisplayMessageStore } from '@/state/useDisplayMessageStore';

const PRIORITY_TONE: Record<string, string> = {
  AlwaysFront: 'bg-rose-700 text-rose-50',
  InFront: 'bg-amber-700 text-amber-50',
  NormalCycle: 'bg-slate-700 text-slate-100',
};

export function DisplayMessagesTab(): JSX.Element {
  const stationId = useStationStore((s) => s.stationId);
  const messages = useDisplayMessageStore((s) => s.messages);
  const refresh = useDisplayMessageStore((s) => s.refresh);
  const acknowledge = useDisplayMessageStore((s) => s.acknowledge);

  useEffect(() => {
    void refresh(stationId);
  }, [refresh, stationId]);

  return (
    <section
      id="tab-panel-display"
      role="tabpanel"
      aria-label="Display Messages"
      className="space-y-3 p-4"
      data-testid="display-messages-tab"
    >
      <p className="text-xs text-slate-400">
        Messages the CSMS has pushed for this charging station&rsquo;s display
        (OCPP 2.0.1 SetDisplayMessage). On a physical charger these would
        appear on the screen.
      </p>
      {messages.length === 0 ? (
        <p className="rounded border border-slate-800 bg-slate-900/50 px-3 py-6 text-center text-sm text-slate-500" data-testid="display-empty">
          No display messages.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="display-list">
          {messages.map((m) => (
            <li
              key={m.id}
              className="flex items-start justify-between gap-3 rounded border border-slate-800 bg-slate-900/50 px-3 py-2"
              data-testid={`display-row-${m.id}`}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                  <span className="font-mono text-slate-500">#{m.id}</span>
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                      PRIORITY_TONE[m.priority] ?? 'bg-slate-700 text-slate-100'
                    }`}
                  >
                    {m.priority}
                  </span>
                  {m.state ? (
                    <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-300">
                      {m.state}
                    </span>
                  ) : null}
                  <span className="text-[10px] uppercase text-slate-500">{m.message.format}</span>
                </div>
                <div className="mt-1 break-words text-sm text-slate-100" data-testid={`display-content-${m.id}`}>
                  {m.message.content}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  void acknowledge(stationId, m.id);
                }}
                className="shrink-0 rounded bg-slate-700 px-2 py-1 text-xs text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
                data-testid={`display-ack-${m.id}`}
              >
                Acknowledge
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
